import { useMemo } from "react";
import { Link } from "react-router-dom";
import { describeError } from "../../api/client";
import type { AdminSegmentSummary } from "../../api/types";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { RecordList } from "../../components/RecordList";
import { invalidateExact } from "../../state/queryClient";
import { useQuery } from "../../state/useQuery";
import { listSegments } from "./api";
import { summarizeCriteria } from "./criteria";
import { SegmentCreateForm } from "./SegmentCreateForm";

/**
 * `/segmentos` — segments list (redesign-panel-hubspot-mailgun, PR 13, tasks 13.1/13.2,
 * design.md §5.4). Migrated onto the shared `DataTable`/`RecordList`/`FeedbackPanel`
 * primitives — no filters, search, or pagination are added because `GET panel/segments`
 * returns an unbounded, un-paginated array today (task 13.1 "pagination only when
 * API-backed": there is no server-side page/size to restore).
 *
 * panel-segment-crud: segments used to be v1 read-only for every role (design.md §5.4's
 * original "The surface stays read-only in this change" decision) — that gap is now closed.
 * `SegmentCreateForm` is mounted unconditionally (same convention as
 * `TemplatesScreen`/`TemplateCreateForm`): it is self-contained, gates itself to `Operador`
 * via its own `RequireRole`, and stays entirely hidden for `Consulta`.
 */
export function SegmentsScreen() {
  const { data, error, loading, stale, refetch } = useQuery<AdminSegmentSummary[]>({
    key: ["segments"],
    queryFn: () => listSegments(),
  });

  const columns: DataTableColumn<AdminSegmentSummary>[] = useMemo(
    () => [
      {
        key: "nombre",
        header: "Nombre",
        render: (row) => <Link to={`/segmentos/${row.segmentId}`}>{row.nombre}</Link>,
      },
      {
        key: "memberCount",
        header: "Miembros actuales (resueltos)",
        render: (row) => <span className="v2-u-tabular-nums">{row.memberCount}</span>,
      },
      { key: "criterio", header: "Criterio", render: (row) => summarizeCriteria(row.criterio) },
    ],
    [],
  );

  const desktopColumns: DataTableColumn<AdminSegmentSummary>[] = useMemo(
    () => [
      ...columns,
      {
        key: "acciones",
        header: "Acción",
        render: (row) => <Link to={`/segmentos/${row.segmentId}`}>Ver detalle</Link>,
      },
    ],
    [columns],
  );

  return (
    <section aria-label="Segmentos" className="v2-segments-list">
      <h1>Segmentos</h1>

      {/* Codex automated review on commit 25b7482 (P2, confirmed by the
          orchestrator reading the code): `refetch()` alone only refreshes
          THIS screen's own `["segments"]` `useQuery` — it never invalidates
          `SegmentSelector`'s (`features/campaigns/SegmentSelector.tsx`)
          entirely separate `["segment-options"]` cache key, so a segment
          created here could stay invisible in an already-open (or
          later-opened) campaign editor's segment dropdown until something
          else happened to trigger that specific revalidation.
          `invalidateExact` is the existing public eviction primitive
          (`state/queryClient.ts`, same one `SegmentDetailScreen` already
          uses) — reused here rather than inventing a new mechanism. */}
      <SegmentCreateForm
        onCreated={() => {
          refetch();
          invalidateExact([["segment-options"]]);
        }}
      />

      {loading && <p role="status">Cargando segmentos…</p>}

      {!data && error !== undefined && (
        <FeedbackPanel
          variant="error"
          message={describeError(error, "No se pudo cargar el listado de segmentos.")}
          onRetry={refetch}
        />
      )}

      {data && (
        <>
          {/* redesign-panel-hubspot-mailgun, PR 16 slice A1 (finding #1, Engram
              #725): `useQuery` already computes `stale` for exactly this case
              (data present, LAST revalidation failed) — every other migrated
              screen that revalidates already surfaces it via this same
              `FeedbackPanel variant="stale"` pattern (Overview,
              CampaignListScreen, CampaignMonitoringTab,
              OperationalHealthScreen, TelemetryScreen); this screen simply
              never destructured/rendered it before this fix. */}
          {stale && (
            <FeedbackPanel variant="stale" message="Datos anteriores de segmentos." onRetry={refetch} />
          )}

          <p aria-live="polite">{data.length} segmentos encontrados</p>

          {data.length === 0 ? (
            <FeedbackPanel variant="empty" message="No hay segmentos registrados." />
          ) : (
            <>
              {/* Segmentos UI audit 2026-09-15, finding #5: the page's own <h1>
                  already says "Segmentos" — the table's visible caption
                  used to repeat that exact word, reading as a redundant
                  second heading. `ariaLabel` alone still gives the table its
                  accessible name for screen readers. */}
              <DataTable
                ariaLabel="Segmentos"
                caption="Resultados"
                columns={desktopColumns}
                rows={data}
                rowKey={(row) => row.segmentId}
              />
              <RecordList
                ariaLabel="Segmentos (vista móvil)"
                columns={columns}
                rows={data}
                rowKey={(row) => row.segmentId}
                recordLabel={(row) => row.nombre}
                renderFooter={(row) => <Link to={`/segmentos/${row.segmentId}`}>Ver detalle</Link>}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
